import * as React from 'react'
import type { CSSProperties } from 'react'
import { type VariantProps } from 'class-variance-authority'
import { cn } from '../lib'
import { buttonVariants } from './button-variants'
import { designTokens } from '../lib/design-tokens'
import { typographyStyle } from '../lib/catalog'

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

type LegacyVariant =
  | 'primary'
  | 'primaryMobile'
  | 'primaryMobileB2B'
  | 'secondaryAssist'
  | 'secondarySubmit'
  | 'secondarySubmitMobile'
  | 'tertiaryCancel'
  | 'tertiaryCancelIcon'
  | 'tertiaryMovement'
  | 'destructiveCancelRenewal'

const legacyVariants = new Set<LegacyVariant>([
  'primary',
  'primaryMobile',
  'primaryMobileB2B',
  'secondaryAssist',
  'secondarySubmit',
  'secondarySubmitMobile',
  'tertiaryCancel',
  'tertiaryCancelIcon',
  'tertiaryMovement',
  'destructiveCancelRenewal',
])

const legacyVariantStyles: Record<
  LegacyVariant,
  {
    background: string
    hoverBackground?: string
    activeBackground?: string
    text: string
    borderColor?: string
    borderWidth?: string
    borderStyle?: string
    radius: string
    paddingX: string
    paddingY: string
  }
> = {
  primary: {
    background: designTokens.colors.background.actionPrimary,
    hoverBackground: designTokens.colors.background.actionPrimaryHover,
    activeBackground: designTokens.colors.background.actionPrimaryPressed,
    text: designTokens.colors.text.inverse,
    radius: designTokens.borders.radius.lg,
    paddingX: designTokens.spacing.lg,
    paddingY: designTokens.spacing.xs,
  },
  primaryMobile: {
    background: designTokens.colors.neutral.grey,
    hoverBackground: designTokens.colors.background.actionPrimaryMobileHover,
    activeBackground: designTokens.colors.background.actionPrimaryMobilePressed,
    text: designTokens.colors.text.inverse,
    radius: designTokens.borders.radius.lg,
    paddingX: designTokens.spacing.lg,
    paddingY: designTokens.spacing.button.primaryMobileY,
  },
  primaryMobileB2B: {
    background: designTokens.colors.background.actionPrimary,
    hoverBackground: designTokens.colors.background.actionPrimaryHover,
    activeBackground: designTokens.colors.background.actionPrimaryPressed,
    text: designTokens.colors.text.inverse,
    radius: designTokens.borders.radius.lg,
    paddingX: designTokens.spacing.lg,
    paddingY: designTokens.spacing.button.primaryMobileY,
  },
  secondaryAssist: {
    background: designTokens.colors.background.surface,
    hoverBackground: designTokens.colors.background.actionSecondaryHover,
    activeBackground: designTokens.colors.background.actionSecondaryPressed,
    text: designTokens.colors.text.primary,
    radius: designTokens.borders.radius.md,
    paddingX: designTokens.spacing.lg,
    paddingY: designTokens.spacing.md,
  },
  secondarySubmit: {
    background: designTokens.colors.neutral.grey,
    hoverBackground: designTokens.colors.background.actionSecondarySubmitHover,
    activeBackground: designTokens.colors.background.actionSecondarySubmitPressed,
    text: designTokens.colors.text.inverse,
    radius: designTokens.borders.radius.lg,
    paddingX: designTokens.spacing.lg,
    paddingY: designTokens.spacing.button.submitY,
  },
  secondarySubmitMobile: {
    background: designTokens.colors.neutral.grey,
    hoverBackground: designTokens.colors.background.actionSecondarySubmitHover,
    activeBackground: designTokens.colors.background.actionSecondarySubmitPressed,
    text: designTokens.colors.text.inverse,
    radius: designTokens.borders.radius.md,
    paddingX: designTokens.spacing.lg,
    paddingY: designTokens.spacing.button.submitMobileYCompact,
  },
  tertiaryCancel: {
    background: designTokens.colors.background.page,
    hoverBackground: designTokens.colors.background.actionTertiaryCancelHover,
    activeBackground: designTokens.colors.background.actionTertiaryCancelPressed,
    text: designTokens.colors.text.primary,
    radius: designTokens.borders.radius.lg,
    paddingX: designTokens.spacing.xxxl,
    paddingY: designTokens.spacing.button.cancelY,
  },
  tertiaryCancelIcon: {
    background: designTokens.colors.background.transparent,
    hoverBackground: designTokens.colors.background.transparent,
    activeBackground: designTokens.colors.background.transparent,
    text: designTokens.colors.text.iconMuted,
    radius: designTokens.borders.radius.lg,
    paddingX: designTokens.spacing.none,
    paddingY: designTokens.spacing.none,
  },
  tertiaryMovement: {
    background: designTokens.colors.background.transparent,
    hoverBackground: designTokens.colors.background.actionTertiaryMovementHover,
    activeBackground: designTokens.colors.background.actionTertiaryMovementPressed,
    text: designTokens.colors.text.primary,
    borderColor: designTokens.colors.border.strong,
    borderWidth: designTokens.borders.widths.thin,
    borderStyle: designTokens.borders.styles.solid,
    radius: designTokens.borders.radius.lg,
    paddingX: designTokens.spacing.lg,
    paddingY: designTokens.spacing.button.cancelY,
  },
  destructiveCancelRenewal: {
    background: designTokens.colors.background.surface,
    hoverBackground: designTokens.colors.background.actionDestructiveHover,
    activeBackground: designTokens.colors.background.actionDestructivePressed,
    text: designTokens.colors.text.primary,
    radius: designTokens.borders.radius.md,
    paddingX: designTokens.spacing.lg,
    paddingY: designTokens.spacing.button.destructiveCancelY,
  },
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, style, children, ...props }, ref) => {
    const legacyVariant =
      variant && legacyVariants.has(variant as LegacyVariant) ? (variant as LegacyVariant) : null
    const legacyStyle = legacyVariant ? legacyVariantStyles[legacyVariant] : null
    type ButtonTokenStyle = CSSProperties & { [key in `--button-${string}`]?: string }
    const baseTokenStyle: ButtonTokenStyle = {
      '--button-ring': designTokens.colors.border.inputFocus,
      '--button-ring-offset': designTokens.colors.background.surface,
    }
    const tokenStyle: ButtonTokenStyle | undefined = legacyStyle
      ? {
          ...baseTokenStyle,
          '--button-bg': legacyStyle.background,
          '--button-bg-hover': legacyStyle.hoverBackground ?? legacyStyle.background,
          '--button-bg-active': legacyStyle.activeBackground ?? legacyStyle.background,
          '--button-fg': legacyStyle.text,
          borderColor: legacyStyle.borderColor ?? designTokens.colors.background.transparent,
          borderWidth: legacyStyle.borderWidth ?? designTokens.borders.widths.none,
          borderStyle: legacyStyle.borderStyle ?? designTokens.borders.styles.solid,
          borderRadius: legacyStyle.radius,
          padding: `${legacyStyle.paddingY} ${legacyStyle.paddingX}`,
          minHeight: designTokens.sizes.height.fit,
          ...typographyStyle(designTokens.typography.button),
        }
      : baseTokenStyle

    const combinedClassName = cn(buttonVariants({ variant, size, className }))
    const combinedStyle = { ...tokenStyle, ...style }

    if (asChild && React.isValidElement(children)) {
      const child = children as React.ReactElement<{ className?: string; style?: CSSProperties }>
      return React.cloneElement(child, {
        className: cn(combinedClassName, child.props.className),
        style: { ...combinedStyle, ...child.props.style },
        ...props,
      })
    }

    return (
      <button className={combinedClassName} ref={ref} style={combinedStyle} {...props}>
        {children}
      </button>
    )
  }
)
Button.displayName = 'Button'

export { Button }
